import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import CompactDatePicker from '../ui/compact-date-picker';
import TimePickerDrawer from '../ui/time-picker-drawer';
import {useAvailabilityViewModel} from "../../view-model/professional/availability-view-model";
import {observer} from "mobx-react-lite";
import { formatDateInTimeZone } from '../../utils/timezone';
import { useTranslation } from 'react-i18next';


const OneTimeSlotManager: React.FC = observer(() => {

  const viewModel = useAvailabilityViewModel();
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold">{t('availability.oneTimeSlots', 'Créneaux ponctuels')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('availability.oneTimeSlotsDescription', 'Ajoutez des créneaux pour des dates spécifiques')}
        </p>
      </div>
      
      <div className="p-4 space-y-4 flex-1 overflow-y-auto">
        {/* Formulaire pour ajouter un créneau */}
        <div className="space-y-3 mb-6 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="grid grid-cols-1 gap-3">
            <CompactDatePicker
              label={t('availability.date', 'Date') || 'Date'}
              id="date"
              name="date"
              value={viewModel.getStartDateOneTime}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setStartDateOneTime(e.target.value)}
              min={formatDateInTimeZone(new Date(), 'yyyy-MM-dd')}
              required
            />
            
            <div className="flex flex-col sm:flex-row gap-2">
              <TimePickerDrawer
                label={t('availability.startTime', 'Heure de début') || 'Heure de début'}
                id="start-time"
                name="start-time"
                value={viewModel.getStartTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setStartTime(e.target.value)}
                step={1800} // 30 minutes
                required
              />

              <TimePickerDrawer
                  label={t('availability.endTime', 'Heure de fin') || 'Heure de fin'}
                  id="end-time"
                  name="end-time"
                  value={viewModel.getEndTime}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setEndTime(e.target.value)}
                  step={1800} // 30 minutes
                  required
                  withButton={false}
              />
              
            </div>

            {viewModel.getError && <div className="flex text-red-700 justify-center">{viewModel.getError}</div>}
            
            {/* Bouton d'ajout en dessous pour mobile et desktop */}
            <button
                disabled={!viewModel.getStartTime || !viewModel.getEndTime}
              onClick={() => viewModel.addOneTimeSlotInList()}
              className={`mt-2 w-full flex items-center justify-center text-white py-2 rounded  transition-colors
                  ${!viewModel.getStartTime || !viewModel.getEndTime
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90'
                  }
              `}
              aria-label={t('availability.addSlot') || 'Add slot'}
            >
              <Plus size={16} className="mr-2" />
              {t('availability.add')}
            </button>

          </div>

          {/* Liste des créneaux */}
          <div>

            {viewModel.addOneTimeSlots.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {t('availability.noOneTimeSlotAdded', 'Aucun créneau ponctuel ajouté')}
                </p>
            ) : (
                <div className="space-y-2">
                  {viewModel.getAddOneTimeSlots.map((slot, i) => (
                      <div
                          key={i}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{viewModel.formatDate(slot.date)}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {slot.startTime} - {slot.endTime}
                          </div>
                        </div>
                        <button
                            onClick={() => viewModel.removeOneTimeSlotOfList(i)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <X size={18} />
                        </button>
                      </div>
                  ))}
                </div>
            )}
          </div>
          {viewModel.addOneTimeSlots.length > 0 && (
              <Button
                  onClick={() => viewModel.handleAddOneTimeSlot()}
                  className="w-full justify-center mt-2 py-3 sm:py-2 text-base sm:text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t('availability.validate', 'Valider')}
              </Button>
          )}

        </div>
      </div>
    </div>
  );
});

export default OneTimeSlotManager;
